import { useQuery } from '@tanstack/react-query'
import { axiosInstance } from '../helpers'
import { normalizeListResponse } from '../helpers/companyDocumentList'
import { OrganizationContractTypeCoverage } from '../models/Company/types'

const QUERY_ROOT = ['organizationCertificateTypeCoverage'] as const

export interface OrganizationCertificateTypeCoverageParams {
    organization: number | null
    includeInactive?: boolean
    certificateTypeId?: number | null
    enabled?: boolean
}

interface CertificateCoverageRow
    extends Omit<OrganizationContractTypeCoverage, 'contract_type'> {
    certificate_type: OrganizationContractTypeCoverage['contract_type']
}

const buildCoverageQueryString = (
    params: OrganizationCertificateTypeCoverageParams,
) => {
    if (!params.organization) return ''
    const searchParams = new URLSearchParams()
    searchParams.append('organization', String(params.organization))
    if (params.includeInactive) {
        searchParams.append('include_inactive', 'true')
    }
    if (params.certificateTypeId != null) {
        searchParams.append(
            'certificate_type',
            String(params.certificateTypeId),
        )
    }
    const qs = searchParams.toString()
    return qs ? `?${qs}` : ''
}

export const useOrganizationCertificateTypeCoverage = (
    params: OrganizationCertificateTypeCoverageParams,
) => {
    const queryString = buildCoverageQueryString(params)

    const { data: coverage, isLoading, error, refetch } = useQuery<
        OrganizationContractTypeCoverage[],
        Error
    >({
        queryKey: [
            ...QUERY_ROOT,
            params.organization,
            params.includeInactive,
            params.certificateTypeId,
        ],
        queryFn: async () => {
            const res = await axiosInstance.get<unknown>(
                `/company/organization_certificate_type/coverage/${queryString}`,
            )
            return normalizeListResponse<CertificateCoverageRow>(res.data).map(
                (row) => ({
                    ...row,
                    contract_type: row.certificate_type,
                }),
            )
        },
        enabled: !!params.organization && params.enabled !== false,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    return {
        coverage: coverage ?? [],
        isLoading,
        error,
        refetch,
    }
}
